import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { CTA_COLOR_BY_NAME } from 'constants/colors';
import { CallToAction, ACTIONS } from 'components/CallToAction';
import { CMS_CTA_TYPES } from './constants';

/**
 * 'CmsCta' is being used for both the general cta module:
 *    https://app.contentful.com/spaces/ew96z4wsnz93/content_types/callToActionGeneral/fields
 * and the legacy cta modules: offer checkout, RAF, url, and youtube.
 *    https://app.contentful.com/spaces/ew96z4wsnz93/content_types
 * Because of this, we are handling both cases here. The general cta module properties are:
 *    'copy'
 *    'ctaType'
 *    'color'
 *    'value'
 *    'style'
 * 'ctaType' ensures we properly map the existing cta modules to their correct logic
 * 'value' is the property that is unique to each cta type
 *
 * TODO: This component will get updated to remove the previous ctas in this ticket
 *    https://classpass.atlassian.net/browse/CRO-1011
 */
export class CmsCta extends PureComponent {
  ctaColor(color, isPrimary) {
    return CTA_COLOR_BY_NAME[color] || (isPrimary && CTA_COLOR_BY_NAME.primary_solid) || CTA_COLOR_BY_NAME.default;
  }

  parseProps({ children, copy, ...extraProps }) {
    return {
      ...extraProps,
      children: children || copy,
    };
  }

  renderCallToAction(action, props = this.props) {
    return <CallToAction {...this.parseProps(props)} action={action} />;
  }

  /**
   * The URL CTA has some legacy color handling to normalize
   * the colors to our standard CTA colors.
   */
  renderUrlCallToAction(actionType) {
    const { ctaType, color, extraClass, isPrimary, type, url, ctaValues, ...extraProps } = this.props;

    if (!ctaValues.url && !url) {
      throw new Error(`URL CTA requires ${ctaType ? 'values.url' : 'url'} for ${type}`);
    }

    const ctaClassName = classNames(extraClass, this.ctaColor(color, isPrimary));

    return this.renderCallToAction(actionType, {
      ...extraProps,
      to: ctaValues.url || url,
      extraClass: ctaClassName,
    });
  }

  /**
   * The YouTube CTA has some legacy color handling to normalize
   * the colors to our standard CTA colors.
   */
  renderYoutubeCallToAction() {
    const { ctaType, color, extraClass, isPrimary, theme, type, ctaValues, videoId, ...extraProps } = this.props;

    if (!ctaValues.videoId && !videoId) {
      throw new Error(`Youtube CTA requires ${ctaType ? 'values.videoId' : 'videoId'} for ${type}`);
    }

    const extraClassWithColors = classNames(extraClass, this.ctaColor(color, isPrimary), {
      [CTA_COLOR_BY_NAME.marketing_white]: !color && theme === 'light',
      [CTA_COLOR_BY_NAME.primary]: !color && theme === 'dark',
    });

    return this.renderCallToAction(ACTIONS.SHOW_MODAL, {
      ...extraProps,
      extraClass: extraClassWithColors,
      modalName: 'YouTubeVideoModal',
      modalProps: {
        videoId: ctaValues.videoId || videoId,
      },
    });
  }

  renderCtaTypes(type) {
    const { ctaValues, isExpectedModalActive } = this.props;
    switch (type) {
      case CMS_CTA_TYPES.CLOSE_MODAL:
        if (isExpectedModalActive) {
          return ctaValues.url
            ? this.renderUrlCallToAction(ACTIONS.CLOSE_MODAL)
            : this.renderCallToAction(ACTIONS.CLOSE_MODAL);
        }
        return null;
      case CMS_CTA_TYPES.OFFER_CHECKOUT:
        return this.renderCallToAction(ACTIONS.OFFER_CHECKOUT);
      case CMS_CTA_TYPES.RAF:
        return this.renderCallToAction(ACTIONS.SHOW_FRIENDBUY_WIDGET);
      case CMS_CTA_TYPES.YOUTUBE_VIDEO:
        return this.renderYoutubeCallToAction();
      case CMS_CTA_TYPES.URL:
        return this.renderUrlCallToAction(ACTIONS.LINK);
      default:
        return null;
    }
  }

  render() {
    const { ctaType, type } = this.props;
    return this.renderCtaTypes(ctaType || type);
  }
}

CmsCta.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  copy: PropTypes.string,
  ctaType: PropTypes.string,
  ctaValues: PropTypes.object,
  extraClass: PropTypes.string,
  isExpectedModalActive: PropTypes.bool,
  isPrimary: PropTypes.bool,
  onClick: PropTypes.func,
  promotionAlias: PropTypes.string,
  theme: PropTypes.string,
  type: PropTypes.string,
  url: PropTypes.string,
  videoId: PropTypes.string,
};
